import { useSyncExternalStore } from 'react';

/**
 * KTD13 (RET-1269 U12/U13) — the day 5-8 banner's close, and the narrow
 * meaning of "session" it implements.
 *
 * The escalation from day 5 needs a full-width surface, and `in-page` is the
 * one that already exists for that (`la-expired` reaches it through a placement
 * resolver). What it did NOT have was any way to put it down for a moment while
 * you work — and the ladder cannot simply become dismissible, because under
 * KTD6 nothing here is acknowledgeable: the Centro de Avisos page has not
 * shipped, so a dismissed notice could never be found again.
 *
 * So the close is a LOCAL VISUAL CLOSE and nothing more:
 *
 *  - it hides the banner for this page load, across route changes (the host is
 *    mounted per page, so component state would bring the banner back the
 *    moment the user opened another tab of the app);
 *  - it is gone on the next load, because this module's state dies with the JS
 *    context. `sessionStorage` would NOT do: it survives a reload, and "back
 *    after a reload" is precisely the guarantee that keeps the notice
 *    permanent;
 *  - it never touches the panel entry, the acknowledgment ledger, or the alert
 *    key. Nothing is persisted, per-device or otherwise.
 *
 * Keyed by the alert KEY rather than the id: an episode that ends and returns
 * gets a fresh key, so a close cannot outlive the episode it was aimed at.
 *
 * AGI-885 — a close now notifies its readers. It used to matter only to the
 * card that was closed, which re-renders itself; it is now a fact another
 * surface reads too (the in-page CTA takes the primary back from a critical
 * card the user put down), so the set is an external store.
 */
const closedKeys = new Set<string>();
const subscribers = new Set<() => void>();
// Bumped on every change, so a reader's snapshot is a cheap number.
let version = 0;

function notify(): void {
  version += 1;
  subscribers.forEach((subscriber) => subscriber());
}

function subscribe(subscriber: () => void): () => void {
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}

function getVersion(): number {
  return version;
}

/**
 * Re-renders the caller whenever a close lands (or the test seam resets).
 * The value is only a change counter: read the closes themselves with
 * `isAlertClosedForSession`.
 */
export function useSessionClosedAlertsVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

/** Families whose in-page banner may be put down for the session. */
export const SESSION_CLOSABLE_IN_PAGE_ALERT_IDS = [
  'past-due-early',
  'past-due-seller',
];

export function isSessionClosableAlert(id: string): boolean {
  return SESSION_CLOSABLE_IN_PAGE_ALERT_IDS.includes(id);
}

export function isAlertClosedForSession(key: string): boolean {
  return closedKeys.has(key);
}

export function closeAlertForSession(key: string): void {
  if (closedKeys.has(key)) return;
  closedKeys.add(key);
  notify();
}

/**
 * Test seam only. The set is module state on purpose (see above), and module
 * state is shared across the cases in a file — without this, one test's close
 * would decide the next one's starting position.
 */
export function resetSessionClosedAlerts(): void {
  closedKeys.clear();
  notify();
}
